// Castintech dashboard — Skills view modulu.
// Faz 9b PR7 — loadMeta + renderSkills. Faz 13 — strict TS hardening.
// Faz 23 PR-B — Skill kullanım telemetrisi paneli (PR-A backend görsel katmanı).
//   GET /castintech-api/skills/usage → state.skillUsageRows + skillUsageMeta.
//   Mevcut catalog grid ÜZERİNE küçük tablo paneli ekler — Karpathy
//   "Don't fix what isn't broken": yeni view yerine extend.

import { state } from '../state.js';
import * as Api from '../api.js';
import { apiFetch } from '../api.js';
import { escHtml, escAttr, getGem, $, asEl } from '../utils.js';
import { META_TTL_MS } from '../config.js';
import { openSkillDrawer } from '../ui/drawer.js';
import { createSkeleton, renderError, renderEmpty } from '../ui/state-display.js';
import type { SkillUsageRow, SkillUsageMeta } from '../../types/api';

// F4-A (2026-05-26): error state tracking for visible retry
let _skillsLastError: string | null = null;

// agents/full + skills meta cache. fetch + render dispatcher.
export async function loadMeta(force = false): Promise<void> {
  if (!force && (Date.now() - state.metaCacheAt) < META_TTL_MS && state.agentsFull.length) return;
  try {
    const [aRes, sRes] = await Promise.all([
      Api.getAgentsFull(),
      Api.getSkills(),
    ]);
    if (aRes.ok) {
      const aj = await aRes.json();
      state.agentsFull = aj.agents || [];
    }
    if (sRes.ok) {
      const sj = await sRes.json();
      state.skillsAll = sj.skills || [];
    }
    state.metaCacheAt = Date.now();
    _skillsLastError = null;
    const sb = $('skills-badge');
    if (sb && state.skillsAll.length) {
      sb.textContent = String(state.skillsAll.length);
      sb.style.display = '';
    }
    if (state.currentPage === 'skills') renderSkills();
  } catch (e) {
    _skillsLastError = (e as Error).message || 'Skill verisi alınamadı';
    console.warn('loadMeta failed:', _skillsLastError);
    if (state.currentPage === 'skills') renderSkills();
  }
}

// Faz 23 PR-B — Skill kullanım telemetrisi loader. PR-A backend
// (GET /castintech-api/skills/usage) son 30 gün invocation aggregation.
interface SkillUsageResponse {
  meta: SkillUsageMeta;
  skills: SkillUsageRow[];
}

export async function loadSkillUsage(): Promise<void> {
  try {
    const r = await apiFetch('/castintech-api/skills/usage');
    if (!r.ok) throw new Error('skills/usage HTTP ' + r.status);
    const data = (await r.json()) as SkillUsageResponse;
    state.skillUsageRows = Array.isArray(data.skills) ? data.skills : [];
    state.skillUsageMeta = data.meta || null;
    if (state.currentPage === 'skills') renderSkillUsagePanel();
  } catch (e) {
    console.warn('loadSkillUsage failed:', (e as Error).message);
  }
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const diff = Date.now() - t;
  if (diff < 0) return 'şimdi';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'şimdi';
  if (mins < 60) return `${mins} dk`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} sa`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 gün';
  if (days < 7) return `${days} gün`;
  if (days < 30) return `${Math.floor(days / 7)} hafta`;
  return `${Math.floor(days / 30)} ay`;
}

export function renderSkillUsagePanel(): void {
  const panel = $('skills-usage-panel');
  if (!panel) return;

  const rows = state.skillUsageRows;
  const meta = state.skillUsageMeta;

  if (!rows) {
    // Henüz fetch edilmemiş — boş bırak (loadSkillUsage çağrıldığında dolar).
    panel.innerHTML = '';
    return;
  }

  const total = meta?.total_invocations ?? 0;
  const since = meta?.since ?? 'last 30 days';

  if (rows.length === 0) {
    panel.innerHTML = `
      <div class="skills-usage-head">
        <div class="skills-usage-title">Skill Kullanım Telemetrisi</div>
        <div class="skills-usage-meta">${escHtml(since)} · 0 invocation</div>
      </div>
      <div class="skills-usage-empty">Henüz invocation kaydı yok. PostToolUse hook devreye girince burada görünür.</div>
    `;
    return;
  }

  const tableRows = rows.map(r => `
    <tr>
      <td class="su-name">${escHtml(r.skill_name || '—')}</td>
      <td class="su-count">${r.invocation_count}</td>
      <td class="su-time">${escHtml(formatRelativeTime(r.last_invoked_at))}</td>
      <td class="su-agents">${r.unique_agents}</td>
    </tr>
  `).join('');

  panel.innerHTML = `
    <div class="skills-usage-head">
      <div class="skills-usage-title">Skill Kullanım Telemetrisi</div>
      <div class="skills-usage-meta">${escHtml(since)} · ${total} toplam invocation · ${rows.length} skill</div>
    </div>
    <div class="skills-usage-table-wrap">
      <table class="skills-usage-table">
        <thead>
          <tr>
            <th>Skill</th>
            <th class="su-count-h">Invocation</th>
            <th>Son kullanım</th>
            <th class="su-count-h">Ajan</th>
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
    </div>
  `;
}

export function renderSkills(): void {
  const sub = $('skills-sub');
  const grid = $('skills-grid');
  if (!grid) return;
  // F4-A (2026-05-26): hata durumunda görünür retry, boş cache + hata yok ise skeleton.
  if (!state.skillsAll.length) {
    grid.innerHTML = '';
    if (_skillsLastError) {
      if (sub) sub.textContent = 'hata: ' + _skillsLastError;
      renderError(grid, _skillsLastError, {
        title: 'Skill katalogu yüklenirken sorun oluştu',
        description: _skillsLastError,
        retryLabel: 'Tekrar dene',
        onRetry: () => { _skillsLastError = null; loadMeta(true); },
      });
      renderSkillUsagePanel();
      return;
    }
    if (sub) sub.textContent = 'yükleniyor...';
    const sk = createSkeleton({ rows: 4, height: '120px', rowGap: '12px' });
    grid.appendChild(sk);
    loadMeta(true);
    renderSkillUsagePanel();
    return;
  }
  const host = state.skillsAll.filter(s => s.location === 'host').length;
  const vault = state.skillsAll.filter(s => s.location === 'vault').length;
  if (sub) sub.textContent = `${state.skillsAll.length} skill — ${host} host · ${vault} vault`;

  grid.innerHTML = state.skillsAll.map((s, i) => {
    const allAgents = [...(s.agents_mandatory || []), ...(s.agents_optional || [])];
    const chips = allAgents.slice(0, 12).map(an => {
      const isMandatory = (s.agents_mandatory || []).includes(an);
      const gem = getGem(an);
      return `<span class="sk-agent-chip ${isMandatory ? 'mandatory' : ''} ${gem.cls}">${gem.name || an}</span>`;
    }).join('');
    const more = allAgents.length > 12 ? `<span class="sk-agent-chip">+${allAgents.length - 12}</span>` : '';
    return `
      <div class="skill-card" style="animation-delay:${i * 30}ms" data-action="open-skill" data-skill-name="${escAttr(s.name)}">
        <div class="sk-head">
          <div class="sk-name">${escHtml(s.name)}</div>
          <span class="sk-loc sk-loc-${s.location}">${s.location}</span>
        </div>
        <div class="sk-desc">${escHtml((s.description || '').substring(0, 180))}${(s.description || '').length > 180 ? '…' : ''}</div>
        <div class="sk-agents">${chips}${more || (allAgents.length === 0 ? '<span style="font-size:10px;color:var(--muted)">— ajan yok</span>' : '')}</div>
      </div>`;
  }).join('');

  // Faz 23 PR-B — telemetry paneli her catalog render'ında refresh edilir
  // (mevcut state'e göre; data fetch'i loadSkillUsage tetikler).
  renderSkillUsagePanel();
}

// Faz 10 PR4 — Skills grid delegated click. data-action=open-skill + data-skill-name.
let _skillsDelegated = false;
export function bindSkillsDelegation(): void {
  if (_skillsDelegated) return;
  const grid = $('skills-grid');
  if (!grid) return;
  grid.addEventListener('click', (e: MouseEvent) => {
    const card = asEl(e.target)?.closest<HTMLElement>('[data-action="open-skill"]');
    if (!card || !grid.contains(card)) return;
    const name = card.dataset.skillName;
    if (name) openSkillDrawer(name);
  });
  _skillsDelegated = true;
}
